import React, { FC } from 'react';
import { PrimaryButton } from 'src/components/widgets/buttons';
import { Dialog, MessageDialog } from 'src/components/widgets/dialog';
import { Icon } from 'src/components/widgets/icon';
import Stack from 'src/components/widgets/stack/stack';
import { T } from 'src/shared/helpers';
import CheckCircleOutlineIcon from 'src/shared/material_icons/action/check_circle_outline.svg';
import EditIcon from 'src/shared/material_icons/image/edit.svg';
import ArrowForwardIcon from 'src/shared/material_icons/navigation/arrow_forward.svg';
import SyncIcon from 'src/shared/material_icons/notification/sync.svg';
import StorefrontIcon from 'src/shared/material_icons/places/storefront.svg';
import styled from 'styled-components';
import {
    Body,
    ChannelAvatar,
    ChannelLink,
    ChannelLinkIcon,
    ChannelName,
    ChannelNode,
    ChannelRole,
    Header,
    HeaderCaption,
    HeaderIcon,
    HeaderTitle,
    Item,
    ItemIcon,
    ItemText,
    Layout,
    Overview,
    OverviewChannels,
    SectionTitle
} from './switchDialogParts';
import { SwitchDialogWindow } from './switchDialogWindow';
import { SwitchWizardStep, SwitchWizardStepCount, SwitchWizardStepIndicator } from './switchWizardSteps';
import { TASKSTATUS } from './switchTypes';

const FOOTNOTE_STYLE = { marginTop: '20px', fontSize: '14px', color: '#666' } as const;

/**
 * Most recent completed EDITSUBSCRIPTION task, or null. Strict `>` keeps the earlier task on a taskID tie.
 * Generic over the task shape because the two call sites read it from selections with different field sets.
 */
export const findLastCompletedEditSubscriptionTask = <T extends { taskID: number; taskType: string; state: number }>(tasks: readonly T[]): T | null =>
    tasks
        .filter((task) => task.taskType === 'EDITSUBSCRIPTION' && task.state === TASKSTATUS.Completed)
        .reduce<T | null>((max, task) => (max === null || task.taskID > max.taskID ? task : max), null);

interface CompleteSwitchDialogProps {
    onSwitchProducts: () => void;
    stepCount?: SwitchWizardStepCount;
}

/**
 * Final confirmation once the seller has subscribed to the new plan. Shown from two separate effects — the
 * ?subscriptionComplete=true return and the isSellerMigrating page-refresh path — which stay separate because
 * they differ in trigger, guard ordering and how they clear the URL.
 */
const CompleteSwitchDialog: FC<CompleteSwitchDialogProps> = ({ onSwitchProducts, stepCount }) => (
    <MessageDialog
        showCloseButton={false}
        windowDecorator={SwitchDialogWindow}
        titleHtml={<SwitchWizardStepIndicator step={SwitchWizardStep.SwitchTask} stepCount={stepCount} />}
        message={
            <>
                <h3>{T._('Complete Sales Channel Switch', 'Admin')}</h3>
                <p>
                    {stepCount === 2
                        ? T._('Your account is ready for the sales channel switch.', 'Admin')
                        : T._('Thank you for subscribing to your new plan!', 'Admin')}
                </p>
                <p>{T._('You can now proceed to switch your products to the new sales channel.', 'Admin')}</p>
                <p>{T._('Click the button below to start the product migration process.', 'Admin')}</p>
                <p style={FOOTNOTE_STYLE}>{T._('This process will run in the background and you will be notified when it completes.', 'Admin')}</p>
            </>
        }
        buttons={[
            {
                label: T._('Switch Products', 'Admin'),
                onClick: onSwitchProducts
            }
        ]}
    />
);

const TaskSummary = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid ${(props) => props.theme.app.borderColor};
`;

const TaskLabel = styled.div`
    font-size: 13px;
    color: ${(props) => props.theme.app.textColor};
`;

/** Icon after the label: the arrow points to where the button leads. */
const ViewTaskButton = styled(PrimaryButton)`
    min-width: 220px;
    flex-direction: row-reverse;
`;

/** The translated sentence with the task number in bold: the placeholder is split out so the translation key stays the same. */
const TaskCreatedCaption: FC<{ taskID: number }> = ({ taskID }) => {
    const [before, after = ''] = T._('The switch task #{0} has been created and is now running in the background.', 'Admin').split('{0}');
    return (
        <>
            {before}
            <strong>{taskID}</strong>
            {after}
        </>
    );
};

interface SwitchTaskCreatedDialogProps {
    /** Null only if the backend did not return an ID: the CTA then falls back to the task list. */
    taskID: number | null;
    masterSalesChannelName: string;
    secondarySalesChannelName: string;
    onGoToTask: () => void;
    onClose: () => void;
    stepCount?: SwitchWizardStepCount;
}

/** Last step of the wizard: the switch task exists and runs in the background. The seller stays here until they choose to open it. */
export const SwitchTaskCreatedDialog: FC<SwitchTaskCreatedDialogProps> = ({
    taskID,
    masterSalesChannelName,
    secondarySalesChannelName,
    onGoToTask,
    onClose,
    stepCount
}) => (
    <Dialog
        windowDecorator={SwitchDialogWindow}
        titleHtml={<SwitchWizardStepIndicator step={SwitchWizardStep.SwitchTask} stepCount={stepCount} />}
        onClose={onClose}
    >
        <Layout>
            <Header>
                <HeaderIcon $tone='success'>
                    <CheckCircleOutlineIcon />
                </HeaderIcon>
                <div>
                    <HeaderTitle>{T._('Sales channel switch started', 'Admin')}</HeaderTitle>
                    <HeaderCaption>
                        {taskID != null ? (
                            <TaskCreatedCaption taskID={taskID} />
                        ) : (
                            T._('The switch task has been created and is now running in the background.', 'Admin')
                        )}
                    </HeaderCaption>
                </div>
            </Header>

            <Body $gap='24px'>
                <Overview>
                    <OverviewChannels>
                        <ChannelNode>
                            <ChannelAvatar>
                                <Icon>
                                    <StorefrontIcon />
                                </Icon>
                            </ChannelAvatar>
                            <ChannelName title={masterSalesChannelName}>{masterSalesChannelName}</ChannelName>
                            <ChannelRole>{T._('Current master', 'Admin')}</ChannelRole>
                        </ChannelNode>
                        <ChannelLink>
                            <ChannelLinkIcon>
                                <SyncIcon />
                            </ChannelLinkIcon>
                        </ChannelLink>
                        <ChannelNode>
                            <ChannelAvatar $filled>
                                <Icon>
                                    <StorefrontIcon />
                                </Icon>
                            </ChannelAvatar>
                            <ChannelName title={secondarySalesChannelName}>{secondarySalesChannelName}</ChannelName>
                            <ChannelRole $emphasis>{T._('Switch in progress', 'Admin')}</ChannelRole>
                        </ChannelNode>
                    </OverviewChannels>
                    <TaskSummary>
                        {taskID != null && (
                            <TaskLabel>
                                {T._('Task', 'Admin')} <strong>#{taskID}</strong>
                            </TaskLabel>
                        )}
                        <ViewTaskButton $size='large' onClick={onGoToTask}>
                            <Icon>
                                <ArrowForwardIcon />
                            </Icon>
                            {taskID != null ? T._('View task details', 'Admin') : T._('View tasks', 'Admin')}
                        </ViewTaskButton>
                    </TaskSummary>
                </Overview>

                <Stack $gap='12px'>
                    <SectionTitle>{T._('While the switch is running', 'Admin')}</SectionTitle>
                    <Item>
                        <ItemIcon $variant='warning'>
                            <EditIcon />
                        </ItemIcon>
                        <ItemText>
                            {T._('Avoid working on products', 'Admin')}
                            <span>
                                {T._(
                                    'Avoid importing, creating or editing products until the switch is complete. You can follow its progress in the task details.',
                                    'Admin'
                                )}
                            </span>
                        </ItemText>
                    </Item>
                </Stack>
            </Body>
        </Layout>
    </Dialog>
);

export default CompleteSwitchDialog;
